import styles from './index.module.css'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { NativeTag } from 'types/track'

import { Badge, Flex, Text, Tooltip } from 'components/ds'
import type { SearchColumnProps } from 'components/search/search-columns/types'

const CHAR_WIDTH = 7
const TAG_H_PADDING = 16
const BADGE_GAP = 4
const PLUS_BADGE_MIN_WIDTH = 32
const MAX_VISIBLE_TAGS = 7

const TagsColumnComponent = ({ info }: SearchColumnProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(() =>
    info.column.getSize(),
  )

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const initial = el.getBoundingClientRect().width
    if (initial > 0) setContainerWidth(initial)

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width
      if (width > 0) setContainerWidth(width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Extract tags and create stable key for memoization
  const rawTags = info.getValue<NativeTag[]>() || []
  const tagsKey = rawTags.map(t => t.id).join(',')
  const tags = useMemo(() => rawTags, [tagsKey])

  // Compute cutoff index - only recalculates when tags content or width changes
  const cutoff = useMemo(() => {
    if (tags.length === 0) return 0

    let usedWidth = 0
    let result = 0

    for (let i = 0; i < tags.length && i < MAX_VISIBLE_TAGS; i++) {
      const tagWidth = tags[i].name.length * CHAR_WIDTH + TAG_H_PADDING
      const remaining = tags.length - i - 1
      const plusWidth = remaining > 0 ? PLUS_BADGE_MIN_WIDTH + BADGE_GAP : 0
      const gap = i > 0 ? BADGE_GAP : 0

      if (usedWidth + gap + tagWidth + plusWidth <= containerWidth) {
        usedWidth += gap + tagWidth
        result = i + 1
      } else {
        break
      }
    }

    return Math.max(1, Math.min(result, MAX_VISIBLE_TAGS))
  }, [tags, containerWidth])

  const hiddenCount = tags.length - cutoff

  return (
    <div ref={containerRef} className={styles.container}>
      <Flex gap="1" wrap="nowrap">
        {tags.slice(0, cutoff).map(tag => (
          <Badge key={tag.id} color="gray" variant="soft">
            {tag.name}
          </Badge>
        ))}
        {hiddenCount > 0 && (
          <Tooltip
            content={
              <Flex direction="column" gap="1">
                {tags.slice(cutoff).map(tag => (
                  <Text key={tag.id} size="1">
                    {tag.name}
                  </Text>
                ))}
              </Flex>
            }
            side="top"
          >
            <Badge
              color="gray"
              variant="soft"
              className={styles.hoverableBadge}
            >
              +{hiddenCount}
            </Badge>
          </Tooltip>
        )}
      </Flex>
    </div>
  )
}

export const TagsColumn = React.memo(TagsColumnComponent)
TagsColumn.displayName = 'TagsColumn'
